// ─── PRICING ENGINE ──────────────────────────────────────────────────────────
// Replicates exact Excel formula chain from WJ_Watch_Collection_2026_CLAUDE.xlsx
// All functions are pure and stateless - pass settings object with FX rates/fees

// Psychological rounding: matches Excel ROUND(x,0)+CHOOSE(MOD(ROUND(x,0),10)+1,-1,-2,3,2,1,0,-1,2,1,0)
export function psychRound(x) {
  const r = Math.round(x)
  const adj = [-1, -2, 3, 2, 1, 0, -1, 2, 1, 0]
  return r + adj[((r % 10) + 10) % 10]
}

// Default settings
export const DEFAULT_SETTINGS = {
  fx_usd_gbp: 1.3,
  fx_usd_eur: 1.08,
  fee_3pl: 1.0,
  fee_pp: 3.5,
}

// ─── COST CHAIN ──────────────────────────────────────────────────────────────

export function getDuty(p) {
  return (parseFloat(p.factory_cost) || 0) * 0.1
}

export function getLandedUSD(p) {
  const cost = parseFloat(p.factory_cost) || 0
  const pkg = parseFloat(p.packaging_cost) || 0
  const freight = parseFloat(p.freight) || 0
  const duty = getDuty(p)
  return cost + pkg + duty + freight
}

export function getLandedGBP(p, settings = DEFAULT_SETTINGS) {
  return getLandedUSD(p) / (settings.fx_usd_gbp || 1.3)
}

export function getLandedEUR(p, settings = DEFAULT_SETTINGS) {
  return getLandedUSD(p) / (settings.fx_usd_eur || 1.08)
}

export function getBaseLanded(p, currency, settings = DEFAULT_SETTINGS) {
  if (currency === 'GBP') return getLandedGBP(p, settings)
  if (currency === 'EUR') return getLandedEUR(p, settings)
  return getLandedUSD(p)
}

// ─── OFF-PRICE RETAIL ────────────────────────────────────────────────────────
// Wholesale = Landed GBP × 2.5
// Calc RRP = Wholesale × 2.4
// Rounded RRP = psychRound(Calc RRP)
// Margin = (Wholesale - Landed) / Wholesale

export function getOffPriceWS(p, currency, settings = DEFAULT_SETTINGS) {
  return getBaseLanded(p, currency, settings) * 2.5
}

export function getOffPriceCalcRRP(p, currency, settings = DEFAULT_SETTINGS) {
  return getOffPriceWS(p, currency, settings) * 2.4
}

export function getOffPriceRRP(p, currency, settings = DEFAULT_SETTINGS) {
  return psychRound(getOffPriceCalcRRP(p, currency, settings))
}

export function getOffPriceMargin(p, currency, settings = DEFAULT_SETTINGS) {
  const ws = getOffPriceWS(p, currency, settings)
  const landed = getBaseLanded(p, currency, settings)
  return ws > 0 ? (ws - landed) / ws : 0
}

// ─── STANDARD WHOLESALE ──────────────────────────────────────────────────────
// Wholesale = Landed GBP × 3
// Calc RRP = Landed GBP × 14
// Rounded RRP = psychRound(Calc RRP)
// RRP Ex VAT = Calc RRP / 1.2
// Margin = (Wholesale - Landed) / Wholesale

export function getStdWS(p, currency, settings = DEFAULT_SETTINGS) {
  return getBaseLanded(p, currency, settings) * 3
}

export function getStdCalcRRP(p, currency, settings = DEFAULT_SETTINGS) {
  return getBaseLanded(p, currency, settings) * 14
}

export function getStdRRP(p, currency, settings = DEFAULT_SETTINGS) {
  return psychRound(getStdCalcRRP(p, currency, settings))
}

export function getStdRRPexVAT(p, currency, settings = DEFAULT_SETTINGS) {
  return getStdCalcRRP(p, currency, settings) / 1.2
}

export function getStdMargin(p, currency, settings = DEFAULT_SETTINGS) {
  const ws = getStdWS(p, currency, settings)
  const landed = getBaseLanded(p, currency, settings)
  return ws > 0 ? (ws - landed) / ws : 0
}

// ─── TRAVEL RETAIL ───────────────────────────────────────────────────────────
// TR Calc RRP = Std Calc RRP × 0.8
// TR Rounded RRP = psychRound(TR Calc RRP)
// TR Supply Price = TR Rounded RRP × 0.35
// TR Margin = (TR Supply - Landed) / TR Supply

export function getTRCalcRRP(p, currency, settings = DEFAULT_SETTINGS) {
  return getStdCalcRRP(p, currency, settings) * 0.8
}

export function getTRRRP(p, currency, settings = DEFAULT_SETTINGS) {
  return psychRound(getTRCalcRRP(p, currency, settings))
}

export function getTRSupply(p, currency, settings = DEFAULT_SETTINGS) {
  return getTRRRP(p, currency, settings) * 0.35
}

export function getTRMargin(p, currency, settings = DEFAULT_SETTINGS) {
  const supply = getTRSupply(p, currency, settings)
  const landed = getBaseLanded(p, currency, settings)
  return supply > 0 ? (supply - landed) / supply : 0
}

// ─── D2C ─────────────────────────────────────────────────────────────────────
// D2C RRP = Std Rounded RRP
// Cost + 3PL = Landed + 3PL picking fee
// Cost + 3PL + P&P = Landed + 3PL + P&P fee
// Margin no free P&P = (RRP - Cost+3PL+PP) / RRP
// Margin free P&P = (RRP - Cost+3PL) / RRP
// 10% Disc RRP = Std Calc RRP × 0.9
// 30% Disc RRP = Std Calc RRP × 0.7

export function getD2CRRP(p, currency, settings = DEFAULT_SETTINGS) {
  return getStdRRP(p, currency, settings)
}

export function getD2CCostNoPP(p, currency, settings = DEFAULT_SETTINGS) {
  return getBaseLanded(p, currency, settings) + (settings.fee_3pl || 1)
}

export function getD2CCostWithPP(p, currency, settings = DEFAULT_SETTINGS) {
  return getBaseLanded(p, currency, settings) + (settings.fee_3pl || 1) + (settings.fee_pp || 3.5)
}

export function getD2CMarginNoPP(p, currency, settings = DEFAULT_SETTINGS) {
  const rrp = getD2CRRP(p, currency, settings)
  return rrp > 0 ? (rrp - getD2CCostWithPP(p, currency, settings)) / rrp : 0
}

export function getD2CMarginFreePP(p, currency, settings = DEFAULT_SETTINGS) {
  const rrp = getD2CRRP(p, currency, settings)
  return rrp > 0 ? (rrp - getD2CCostNoPP(p, currency, settings)) / rrp : 0
}

export function getD2C10PctRRP(p, currency, settings = DEFAULT_SETTINGS) {
  return getStdCalcRRP(p, currency, settings) * 0.9
}

export function getD2C10PctRRPexVAT(p, currency, settings = DEFAULT_SETTINGS) {
  return getD2C10PctRRP(p, currency, settings) / 1.2
}

export function getD2C10MarginNoPP(p, currency, settings = DEFAULT_SETTINGS) {
  const rrp = getD2C10PctRRP(p, currency, settings)
  return rrp > 0 ? (rrp - getD2CCostWithPP(p, currency, settings)) / rrp : 0
}

export function getD2C10MarginFreePP(p, currency, settings = DEFAULT_SETTINGS) {
  const rrp = getD2C10PctRRP(p, currency, settings)
  return rrp > 0 ? (rrp - getD2CCostNoPP(p, currency, settings)) / rrp : 0
}

export function getD2C30PctRRP(p, currency, settings = DEFAULT_SETTINGS) {
  return getStdCalcRRP(p, currency, settings) * 0.7
}

export function getD2C30PctRRPexVAT(p, currency, settings = DEFAULT_SETTINGS) {
  return getD2C30PctRRP(p, currency, settings) / 1.2
}

export function getD2C30MarginNoPP(p, currency, settings = DEFAULT_SETTINGS) {
  const rrp = getD2C30PctRRP(p, currency, settings)
  return rrp > 0 ? (rrp - getD2CCostWithPP(p, currency, settings)) / rrp : 0
}

export function getD2C30MarginFreePP(p, currency, settings = DEFAULT_SETTINGS) {
  const rrp = getD2C30PctRRP(p, currency, settings)
  return rrp > 0 ? (rrp - getD2CCostNoPP(p, currency, settings)) / rrp : 0
}

// ─── STOCK HELPERS ───────────────────────────────────────────────────────────

export function getStockTotal(p) {
  return (parseInt(p.stock_uk) || 0) + (parseInt(p.stock_de) || 0)
}

export function getWeeksCover(p) {
  const ws = parseFloat(p.avg_weekly_sales) || 2
  const total = getStockTotal(p)
  return ws > 0 ? Math.floor(total / ws) : null
}

export function getReorderQty(p) {
  const ws = parseFloat(p.avg_weekly_sales) || 2
  return Math.ceil(ws * 6) // 6 weeks of cover
}

export function getStockStatus(p) {
  const total = getStockTotal(p)
  if (total === 0) return { label: 'Out of stock', type: 'out' }
  if (total <= 10) return { label: `Low: ${total}`, type: 'low' }
  return { label: `In stock: ${total}`, type: 'in' }
}

// ─── CURRENCY HELPERS ────────────────────────────────────────────────────────

export const CURRENCY_SYMBOLS = { GBP: '£', USD: '$', EUR: '€' }

export function formatPrice(amount, currency) {
  const sym = CURRENCY_SYMBOLS[currency] || '£'
  return `${sym}${parseFloat(amount).toFixed(2)}`
}

export function formatPct(n) {
  return `${(n * 100).toFixed(1)}%`
}

export function marginClass(n) {
  if (n > 0.5) return 'margin-good'
  if (n > 0.3) return 'margin-warn'
  return 'margin-bad'
}
